import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  Info, Briefcase, Sparkle, MapPin, ArrowSquareOut, ArrowsClockwise, WarningCircle,
} from "@phosphor-icons/react";

/**
 * Read-only coach view of a coachee's vacancy search results.
 * Coach can RESET the counter so the coachee can search again.
 */
export default function VacaturesCoachPanel({ coacheeId, coacheeName }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/coachees/${coacheeId}/vacatures`);
      setDoc(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get(`/coachees/${coacheeId}/vacatures`);
        if (!cancelled) setDoc(data);
      } catch (err) {
        if (!cancelled) setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [coacheeId]);

  const handleReset = async () => {
    setResetting(true);
    setError("");
    try {
      const { data } = await api.post(`/coachees/${coacheeId}/vacatures/reset`);
      setDoc(data);
      setConfirmReset(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setResetting(false);
    }
  };

  const results = doc?.results || [];
  const used = doc?.ai_count ?? 0;
  const max = doc?.ai_max ?? 3;
  const status = doc?.status;

  return (
    <div className="space-y-4 max-w-3xl" data-testid="vacatures-coach-panel">
      <div className="neuro-card-bold p-5 bg-white">
        <div className="flex items-start gap-3">
          <Briefcase size={26} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-display text-lg font-bold tracking-tight">Vacatures</h3>
            <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
              Door de AI (Perplexity) gegenereerde lijst van Nederlandse vacatures
              voor {coacheeName || "deze coachee"} — gebaseerd op CV, breintype,
              alle tests en profielen.
            </p>
            <div className="mt-3 flex items-center gap-4 text-xs text-zinc-500 flex-wrap" data-testid="vac-coach-stats">
              <span>
                Status: <strong className="text-zinc-900">
                  {results.length > 0
                    ? `${results.length} vacatures beschikbaar`
                    : status === "running" ? "Wordt gezocht…"
                    : status === "failed" ? "Mislukt"
                    : "Nog niet gezocht"}
                </strong>
              </span>
              <span className="inline-flex items-center gap-1">
                <Sparkle size={12} className="text-[#1E88E5]" /> {used} van {max} zoekopdrachten gebruikt
              </span>
            </div>
          </div>
          {used > 0 && (
            <button
              type="button"
              onClick={() => setConfirmReset(true)}
              data-testid="vac-coach-reset-btn"
              className="shrink-0 inline-flex items-center gap-2 bg-white text-zinc-900 border-2 border-zinc-900 px-3 py-1.5 text-xs font-semibold tracking-widest uppercase rounded-sm hover:bg-zinc-900 hover:text-white transition-colors"
            >
              <ArrowsClockwise size={14} weight="bold" /> Reset
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm" data-testid="vac-coach-error">{error}</div>
      )}

      {loading ? (
        <div className="text-zinc-500 text-sm">Laden...</div>
      ) : results.length === 0 ? (
        <div className="neuro-card p-6 bg-white" data-testid="vac-coach-empty">
          <div className="flex items-start gap-3">
            <Info size={22} className="text-[#1E88E5] shrink-0 mt-0.5" />
            <div>
              <h4 className="font-display text-base font-bold tracking-tight">
                {status === "running" ? "Wordt gezocht…" : "Nog geen vacatures"}
              </h4>
              <p className="text-sm text-zinc-600 mt-1">
                {status === "running"
                  ? "De AI is bezig — kijk over een minuutje opnieuw."
                  : status === "failed"
                  ? "De vorige zoekopdracht is mislukt. Reset om opnieuw te starten."
                  : `${coacheeName || "De coachee"} heeft nog geen zoekopdracht gestart.`}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="vac-coach-results-list">
          {results.map((job, idx) => (
            <article
              key={`${idx}-${job.url}`}
              className="neuro-card p-4 bg-white"
              data-testid={`vac-coach-result-${idx}`}
            >
              <div className="flex items-start gap-3">
                <span className="shrink-0 inline-flex h-7 w-7 items-center justify-center bg-[#1E88E5] text-white text-xs font-bold rounded-sm">
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <h4 className="font-display text-base font-bold tracking-tight">
                    {job.title}
                  </h4>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-700">
                    <span className="font-medium">{job.company}</span>
                    {job.location && (
                      <span className="inline-flex items-center gap-1 text-zinc-500">
                        <MapPin size={11} /> {job.location}
                      </span>
                    )}
                  </div>
                  {job.fit_explanation && (
                    <p className="text-xs text-zinc-600 mt-2 leading-relaxed">
                      {job.fit_explanation}
                    </p>
                  )}
                  <a
                    href={job.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#1E88E5] hover:text-[#1565C0]"
                  >
                    Vacature openen <ArrowSquareOut size={12} weight="bold" />
                  </a>
                </div>
              </div>
            </article>
          ))}
          {doc?.generated_at && (
            <div className="text-xs text-zinc-400 pt-1">
              Gegenereerd op {new Date(doc.generated_at).toLocaleString("nl-NL")}
            </div>
          )}
        </div>
      )}

      {confirmReset && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="vac-coach-reset-modal" role="dialog" aria-modal="true">
          <div className="bg-white max-w-md w-full p-6 rounded-sm border-2 border-zinc-900">
            <div className="flex items-start gap-3 mb-3">
              <WarningCircle size={24} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
              <h3 className="font-display text-lg font-bold tracking-tight">Vacaturezoeker resetten?</h3>
            </div>
            <ul className="text-sm text-zinc-700 leading-relaxed space-y-2 mb-3 list-disc pl-5">
              <li>De teller gaat terug naar <strong>0</strong>.</li>
              <li>De huidige resultaten worden <strong>gewist</strong>.</li>
              <li>{coacheeName || "De coachee"} kan opnieuw <strong>{max} zoekopdrachten</strong> uitvoeren.</li>
            </ul>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setConfirmReset(false)} disabled={resetting} data-testid="vac-coach-reset-cancel"
                className="px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm border-2 border-zinc-300 hover:border-zinc-500 transition-colors">
                Annuleer
              </button>
              <button type="button" onClick={handleReset} disabled={resetting} data-testid="vac-coach-reset-confirm"
                className="px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm bg-zinc-900 text-white hover:bg-[#1E88E5] transition-colors inline-flex items-center gap-2 disabled:opacity-60">
                <ArrowsClockwise size={14} weight="bold" /> {resetting ? "Resetten…" : "Ja, resetten"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
